/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabase'

function formatDate(date) {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

function getDaysUntil(date) {
  if (!date) return null
  return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24))
}

function AllActions({ profile, orgUsers, onOpenAction }) {
  const [actions, setActions] = useState([])
  const [loading, setLoading] = useState(true)

  const [statusFilter, setStatusFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState('all')
  const [opportunityFilter, setOpportunityFilter] = useState('all')

  useEffect(() => {
    loadActions()
  }, [])

  const loadActions = async () => {
    setLoading(true)

    const { data } = await supabase
      .from('actions')
      .select(`
        *,
        opportunities (
          id,
          title,
          buyer
        )
      `)
      .eq('organisation_id', profile.organisation_id)
      .order('due_date', { ascending: true })

    setActions(data || [])
    setLoading(false)
  }

  const ownerOptions = useMemo(() => {
    const names = new Set()

    actions.forEach(action => {
      const assignedUser = orgUsers.find(u => u.id === action.assigned_to)

      if (assignedUser) {
        names.add(assignedUser.full_name || assignedUser.username)
      }

      if (action.assigned_to_name) {
        names.add(action.assigned_to_name)
      }
    })

    return Array.from(names).sort()
  }, [actions, orgUsers])

  const opportunityOptions = useMemo(() => {
    const opps = new Map()

    actions.forEach(action => {
      if (action.opportunities) {
        opps.set(action.opportunities.id, action.opportunities)
      }
    })

    return Array.from(opps.values()).sort((a, b) =>
      a.title.localeCompare(b.title)
    )
  }, [actions])

  const filteredActions = actions.filter(action => {
    const assignedUser = orgUsers.find(u => u.id === action.assigned_to)
    const ownerName =
      assignedUser?.full_name ||
      assignedUser?.username ||
      action.assigned_to_name ||
      'Unassigned'

    if (statusFilter !== 'all' && action.status !== statusFilter) return false
    if (ownerFilter !== 'all' && ownerName !== ownerFilter) return false
    if (
      opportunityFilter !== 'all' &&
      action.opportunities?.id !== opportunityFilter
    ) {
      return false
    }

    return true
  })

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
      <h1
        style={{
          fontFamily: 'Cormorant Garamond, serif',
          fontSize: '2rem',
          marginBottom: '24px'
        }}
      >
        All Actions
      </h1>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}
      >
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="complete">Complete</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select
          value={ownerFilter}
          onChange={e => setOwnerFilter(e.target.value)}
        >
          <option value="all">All Owners</option>
          {ownerOptions.map(owner => (
            <option key={owner} value={owner}>
              {owner}
            </option>
          ))}
        </select>

        <select
          value={opportunityFilter}
          onChange={e => setOpportunityFilter(e.target.value)}
        >
          <option value="all">All Opportunities</option>
          {opportunityOptions.map(opp => (
            <option key={opp.id} value={opp.id}>
              {opp.title}
            </option>
          ))}
        </select>
      </div>

      {loading && <p>Loading actions...</p>}

      {!loading &&
        filteredActions.map(action => {
          const assignedUser = orgUsers.find(
            u => u.id === action.assigned_to
          )

          const ownerName =
            assignedUser?.full_name ||
            assignedUser?.username ||
            action.assigned_to_name ||
            'Unassigned'

          const daysUntil = getDaysUntil(action.due_date)
          const isOverdue =
            daysUntil !== null &&
            daysUntil < 0 &&
            action.status !== 'complete' &&
            action.status !== 'cancelled'

          return (
            <div
              key={action.id}
              onClick={() =>
                onOpenAction(action.opportunities)
              }
              style={{
                background: 'white',
                border: `1px solid ${
                  isOverdue ? 'var(--red)' : 'var(--green-light)'
                }`,
                borderLeft: `4px solid ${
                  isOverdue ? 'var(--red)' : 'var(--green)'
                }`,
                padding: '16px',
                marginBottom: '12px',
                cursor: 'pointer'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 600,
                      marginBottom: '6px'
                    }}
                  >
                    {action.description}
                  </div>

                  <div
                    style={{
                      fontSize: '0.85rem',
                      color: 'var(--slate)'
                    }}
                  >
                    {action.opportunities?.title}
                  </div>

                  <div
                    style={{
                      fontSize: '0.8rem',
                      marginTop: '6px'
                    }}
                  >
                    Owner: {ownerName}
                  </div>
                </div>

                <div style={{ textAlign: 'right' }}>
                  <div>{formatDate(action.due_date)}</div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: isOverdue ? 'var(--red)' : 'var(--slate)'
                    }}
                  >
                    {action.status}
                  </div>
                </div>
              </div>
            </div>
          )
        })}

      {!loading && filteredActions.length === 0 && (
        <p>No actions found.</p>
      )}
    </div>
  )
}

export default AllActions